import { AbsenceRequestValidationError, absenceDayPartLabel, conditionFactsPayload, type AbsenceRequestField } from '../../features/absence/public';
import { ApiError, type ApiClient } from '../../api/client';
import type {
  StaffingWorker as ApiStaffingWorker,
  TimeOffEntry as ApiTimeOffEntry,
} from '../../api/types';
import type {
  AbsencePort,
  AbsenceSyncSchedule,
  AbsenceTimeOffRow,
  AbsenceWorker,
} from '../../features/absence/public';

function displayDate(value?: string): string {
  return value ? value.slice(0, 10).replaceAll('-', '.') : '—';
}

function mapAbsenceWorker(worker: ApiStaffingWorker): AbsenceWorker {
  return {
    id: worker.id,
    personId: worker.person?.id,
    name: worker.descriptor?.trim() || '이름 미등록',
    workerNumber: worker.workerId || '사번 미등록',
    jobTitle: worker.primaryJob?.jobProfile?.descriptor?.trim() || worker.primaryJob?.businessTitle?.trim() || undefined,
    organization: worker.primaryJob?.supervisoryOrganization,
    photoUrl: worker.person?.profilePhotoUrl,
  };
}

function mapAbsenceTimeOffEntry(entry: ApiTimeOffEntry): AbsenceTimeOffRow {
  const sameDay = entry.startDate === entry.endDate;
  const period = sameDay ? displayDate(entry.startDate) : `${displayDate(entry.startDate)} ~ ${displayDate(entry.endDate)}`;
  const dayPart = absenceDayPartLabel(entry.dayPart);
  return {
    id: entry.id,
    worker: entry.worker.descriptor || '이름 미등록',
    type: entry.timeOffType.descriptor || '휴가 유형 미등록',
    period: dayPart ? `${period} · ${dayPart}` : period,
    days: `${entry.quantity}${['DAY', 'DAYS'].includes(entry.unitOfTime) ? '일' : ['HOUR', 'HOURS'].includes(entry.unitOfTime) ? '시간' : ` ${entry.unitOfTime}`}`,
    status: entry.status,
    source: entry,
  };
}

const calculationErrorFields: Readonly<Record<string, AbsenceRequestField>> = {
  TIME_OFF_INSUFFICIENT_BALANCE: 'quantity',
  TIME_OFF_OVERLAP: 'startDate',
  TIME_OFF_WORKER_INACTIVE: 'startDate',
  TIME_OFF_NO_WORKING_DAYS: 'startDate',
  TIME_OFF_MINIMUM_INCREMENT: 'quantity',
  TIME_OFF_INVALID_DAY_PART: 'dayPart',
  TIME_OFF_REASON_REQUIRED: 'reason',
  TIME_OFF_EVIDENCE_REQUIRED: 'evidenceDocuments',
  TIME_OFF_ALL_AT_ONCE_REQUIRED: 'quantity',
  TIME_OFF_UNIT_MISMATCH: 'timeOffType',
  TIME_OFF_POLICY_UNAVAILABLE: 'startDate',
  TIME_OFF_POLICY_CALENDAR_CONFLICT: 'timeOffType',
  TIME_OFF_USAGE_LIMIT_POLICY_BOUNDARY: 'startDate',
  TIME_OFF_USAGE_LIMIT_UNAVAILABLE: 'startDate',
};

export function createAbsenceHttpAdapter(client: ApiClient): AbsencePort {
  return {
    isDefinitiveMutationFailure(cause) {
      return cause instanceof ApiError && cause.status >= 400 && cause.status < 500 && cause.status !== 408
        && cause.code !== 'IDEMPOTENCY_IN_PROGRESS';
    },
    getSyncSchedule(workerId) {
      return client.requestTenant<AbsenceSyncSchedule>(`/api/hr/v1/{tenant}/workers/${encodeURIComponent(workerId)}/absenceSyncSchedule`);
    },
    updateSyncSchedule(workerId, input) {
      return client.requestTenant<AbsenceSyncSchedule>(`/api/hr/v1/{tenant}/workers/${encodeURIComponent(workerId)}/absenceSyncSchedule`, { method: 'PUT', body: JSON.stringify(input) });
    },
    listCalendar(query) {
      return client.listTimeOffCalendar(query);
    },
    listPublicHolidays(query) {
      const params = new URLSearchParams({ from: query.from, to: query.to });
      return client.requestTenant(`/api/hr/v1/{tenant}/absence/holidayCalendars/korea/days?${params}`);
    },
    async listWorkers() {
      return (await client.listAllWorkers()).data.map(mapAbsenceWorker);
    },
    async listEntries(workerId) {
      return (await client.listTimeOffEntries(workerId)).data.map(mapAbsenceTimeOffEntry);
    },
    async listBalances(workerId, asOf) {
      return (await client.listTimeOffBalances(workerId, asOf)).data;
    },
    requestEvidence: {
      async list(id) { return (await client.requestTenant<{ data: { id: string; displayName: string; scanStatus: string }[] }>(`/api/absenceManagement/v5/{tenant}/timeOffRequests/${encodeURIComponent(id)}/evidence`)).data; },
      async add(id, evidenceDocuments, key) { return (await client.requestTenant<{ data: { id: string; displayName: string; scanStatus: string }[] }>(`/api/absenceManagement/v5/{tenant}/timeOffRequests/${encodeURIComponent(id)}/evidence`, { method: 'POST', headers: { 'Idempotency-Key': key }, body: JSON.stringify({ evidenceDocuments }) })).data; },
      download: (id) => client.downloadDocument(id),
    },
    async listEntryEvidence(id) { return (await client.requestTenant<{ data: { id: string; displayName: string; scanStatus: string }[] }>(`/api/absenceManagement/v5/{tenant}/timeOffEntries/${encodeURIComponent(id)}/evidence`)).data; },
    getRequest(requestId) { return client.getTimeOffRequest(requestId); },
    async listRequests(workerId) {
      return (await client.listTimeOffRequests(workerId)).data;
    },
    async listLedger(workerId, entitlementId) {
      return (await client.listEntitlementTransactions(workerId, entitlementId)).data;
    },
    async listRequestableTimeOffTypes(workerId, asOf) {
      return (await client.listRequestableTimeOffTypes(workerId, asOf)).data;
    },
    async listTimeOffTypes() {
      return (await client.listTimeOffTypes()).data;
    },
    async calculateEntry(workerId, input) {
      return client.requestTenant(`/api/absenceManagement/v5/{tenant}/workers/${encodeURIComponent(workerId)}/timeOffEntryCalculation`, { method: 'POST', body: JSON.stringify(input) });
    },
    async calculateRequest(workerId, input) {
      try {
        return await client.calculateTimeOffRequest(workerId, { ...input, conditionFacts: conditionFactsPayload(input.conditionFacts) });
      } catch (failure) {
        const field = failure instanceof ApiError && failure.status >= 400 && failure.status < 500 && failure.code ? calculationErrorFields[failure.code] : undefined;
        if (field && failure instanceof Error) throw new AbsenceRequestValidationError(failure.message, field);
        throw failure;
      }
    },
    createRequest(workerId, input, idempotencyKey) {
      return client.createTimeOffRequest(workerId, { ...input, conditionFacts: conditionFactsPayload(input.conditionFacts) }, idempotencyKey);
    },
    submitRequest(requestId, idempotencyKey) {
      return client.submitTimeOffRequest(requestId, idempotencyKey);
    },
    async createDirectEntry(workerId, input, idempotencyKey) {
      await client.createDirectTimeOffEntry(workerId, { ...input, conditionFacts: conditionFactsPayload(input.conditionFacts) }, idempotencyKey);
    },
    async correctEntry(entryId, input, idempotencyKey) {
      await client.correctTimeOffEntry(entryId, { ...input, conditionFacts: conditionFactsPayload(input.conditionFacts) }, idempotencyKey);
    },
    async cancelEntry(entryId, reason, idempotencyKey) {
      await client.cancelTimeOffEntry(entryId, reason, idempotencyKey);
    },
    async cancelRequest(requestId, comment, idempotencyKey) {
      await client.cancelTimeOffRequest(requestId, comment, idempotencyKey);
    },
    async changeBalance(workerId, entitlementId, operation, input, idempotencyKey) {
      await client.changeTimeOffBalance(workerId, entitlementId, operation, input, idempotencyKey);
    },
  };
}
